import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import DelegatePicker, { DelegateOption } from './DelegatePicker';

// A compact delegate combobox: shows the picked delegate (or a placeholder) and
// opens a search dropdown to choose/change them. Wraps DelegatePicker for the
// search + list.

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    position: 'relative'
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 44,
    padding: '0 12px',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: '#d7dae0'
    }
  },
  triggerOpen: {
    extend: 'trigger',
    borderColor: '#0b57ff',
    boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
  },
  placeholder: {
    flex: 1,
    fontSize: 14,
    color: '#9ea2ad'
  },
  selectedName: {
    flex: 1,
    minWidth: 0,
    marginLeft: 12,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  chevron: {
    flexShrink: 0,
    marginLeft: 12,
    color: '#9ea2ad',
    fontSize: 12,
    transition: 'transform 0.15s'
  },
  chevronOpen: {
    extend: 'chevron',
    transform: 'rotate(180deg)'
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    right: 0,
    zIndex: 20,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    padding: '12px 12px 18px'
  }
}));

type Props = {
  value?: DelegateOption;
  onChange: (delegate: DelegateOption) => void;
};

function DelegateSelect({ value, onChange }: Props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    // Capture phase so this still fires for clicks inside the modal panel,
    // which stops propagation on its own mousedown handler.
    const onDown = (evt: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(evt.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  return (
    <div className={classes.root} ref={rootRef}>
      <div
        className={open ? classes.triggerOpen : classes.trigger}
        onClick={() => setOpen((o) => !o)}
      >
        {value ? (
          <>
            <ProfileIcon
              name={`${value.firstName} ${value.lastName}`}
              url={value.profileImageUrl ?? undefined}
              size={28}
            />
            <span className={classes.selectedName}>
              {value.firstName} {value.lastName}
            </span>
          </>
        ) : (
          <span className={classes.placeholder}>Select a delegate</span>
        )}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={open ? classes.chevronOpen : classes.chevron}
        />
      </div>
      {open && (
        <div className={classes.dropdown}>
          <DelegatePicker
            onSelect={(delegate) => {
              onChange(delegate);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DelegateSelect;
